/**
 * Tools & MCP Settings Section
 * 
 * This section allows users to:
 * - Configure built-in tools (enable/disable)
 * - Set API keys for external tool services (Tavily, SerpAPI, Firecrawl)
 * - Configure MCP server connections
 * - Set default tool behavior
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
    Button,
    SectionSettingsWrapper,
    TextInputLabelForm,
    BsCheck2Circle,
    BsXCircle,
    BiTrash,
    BsPlus,
    BsGlobe,
    BiSearch,
    BiX,
    AiFillLock,
    toast,
} from '@acme/ui';
import { toolMetadata } from '@acme/util';
import {
    apiKeyTavilyAtom,
    apiKeySerpApiAtom,
    apiKeyFirecrawlAtom,
} from '../../store/setting';
import {
    globalToolsConfigAtom,
    mcpServersConfigAtom,
    type MCPServerUIConfig,
} from '../../store/instruction';
import { useUserSubscription } from '../../hooks';
import { upgradeModalAtom } from '../../store/user';

// =============================================================================
// TOOL SERVICE PROVIDERS CONFIG
// =============================================================================

const TOOL_PROVIDERS = [
    {
        id: 'tavily' as const,
        name: 'Tavily',
        description: 'AI-powered web search API for accurate, up-to-date results',
        url: 'https://tavily.com/',
        placeholder: 'tvly-...',
        icon: '🔍',
        features: ['AI Search', 'News', 'Research', 'Real-time'],
    },
    {
        id: 'serpapi' as const,
        name: 'SerpAPI',
        description: 'Google Search results API with structured data',
        url: 'https://serpapi.com/',
        placeholder: 'serpapi-...',
        icon: '🌐',
        features: ['Google Search', 'Images', 'Maps', 'Shopping'],
    },
    {
        id: 'firecrawl' as const,
        name: 'Firecrawl',
        description: 'Web scraping and crawling API for AI applications',
        url: 'https://firecrawl.dev/',
        placeholder: 'fc-...',
        icon: '🔥',
        features: ['Web Scraping', 'Crawling', 'Markdown', 'Screenshots'],
    },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ToolsSettings: React.FC = () => {
    const { isAllowTools, isAllowMCP } = useUserSubscription();
    const setUpgradeModal = useSetAtom(upgradeModalAtom);

    const handleUpgradeClick = () => {
        setUpgradeModal({ isOpen: true, lastOpen: new Date() });
    };

    // Show upgrade prompt if user doesn't have access
    if (!isAllowTools.allow) {
        return (
            <div className="space-y-6">
                <SectionSettingsWrapper title="Tools & MCP" isShowTitle={true}>
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
                            <AiFillLock className="text-3xl text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Unlock AI Tools & MCP
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-neutral-400 max-w-sm mb-6">
                            {isAllowTools.message}. Get access to web search, code execution, memory, and MCP server integrations.
                        </p>
                        <Button
                            onClick={handleUpgradeClick}
                            className="!bg-blue-600 hover:!bg-blue-700 !text-white !py-2 !px-6 !rounded-xl"
                        >
                            Upgrade to Unlock
                        </Button>
                    </div>
                </SectionSettingsWrapper>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <ToolServicesSection />
            <BuiltInToolsSection />
            <MCPServersSection />
        </div>
    );
};

// =============================================================================
// TOOL SERVICES SECTION
// =============================================================================

const ToolServicesSection: React.FC = () => {
    const [tavilyKey, setTavilyKey] = useAtom(apiKeyTavilyAtom);
    const [serpApiKey, setSerpApiKey] = useAtom(apiKeySerpApiAtom);
    const [firecrawlKey, setFirecrawlKey] = useAtom(apiKeyFirecrawlAtom);
    const [editingProvider, setEditingProvider] = useState<string | null>(null);
    const [tempKey, setTempKey] = useState('');

    const getKeyForProvider = (id: string): string => {
        switch (id) {
            case 'tavily': return (tavilyKey?.key as string) || '';
            case 'serpapi': return (serpApiKey?.key as string) || '';
            case 'firecrawl': return (firecrawlKey?.key as string) || '';
            default: return '';
        }
    };

    const setKeyForProvider = (id: string, key: string) => {
        const keyObj = key ? { id, key, created: new Date() } : null;
        switch (id) {
            case 'tavily': setTavilyKey(keyObj); break;
            case 'serpapi': setSerpApiKey(keyObj); break;
            case 'firecrawl': setFirecrawlKey(keyObj); break;
        }
    };

    const handleSave = (providerId: string) => {
        setKeyForProvider(providerId, tempKey);
        setEditingProvider(null);
        setTempKey('');
    };

    const handleEdit = (providerId: string) => {
        setEditingProvider(providerId);
        setTempKey(getKeyForProvider(providerId));
    };

    const handleRemove = (providerId: string) => {
        setKeyForProvider(providerId, '');
    };

    const maskApiKey = (key: string): string => {
        if (!key || key.length < 8) return key || '';
        return key.slice(0, 4) + '•'.repeat(Math.min(key.length - 8, 12)) + key.slice(-4);
    };

    return (
        <SectionSettingsWrapper title="Tool Services" isShowTitle={true}>
            <div className="space-y-4">
                <p className="text-sm text-gray-500 dark:text-neutral-400 mb-4">
                    Connect external services to enhance your AI tools with real-time data and web access.
                </p>

                <div className="grid gap-3">
                    {TOOL_PROVIDERS.map(provider => {
                        const hasKey = !!getKeyForProvider(provider.id);
                        const isEditing = editingProvider === provider.id;

                        return (
                            <div
                                key={provider.id}
                                className={`
                                    p-4 rounded-xl border transition-colors
                                    ${hasKey
                                        ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50'
                                        : 'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800'
                                    }
                                `}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1">
                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-neutral-800 flex items-center justify-center text-xl">
                                            {provider.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-medium text-gray-900 dark:text-white">
                                                    {provider.name}
                                                </h4>
                                                {hasKey ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                                        <BsCheck2Circle className="text-xs" />
                                                        Connected
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-neutral-400">
                                                        Not configured
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">
                                                {provider.description}
                                            </p>
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {provider.features.map((feature, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="px-2 py-0.5 text-xs rounded-md bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300"
                                                    >
                                                        {feature}
                                                    </span>
                                                ))}
                                            </div>
                                            {hasKey && !isEditing && (
                                                <code className="inline-block mt-2 px-2 py-1 text-xs font-mono bg-white dark:bg-neutral-800 rounded border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-neutral-300">
                                                    {maskApiKey(getKeyForProvider(provider.id))}
                                                </code>
                                            )}
                                        </div>
                                    </div>

                                    {!isEditing ? (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                onClick={() => handleEdit(provider.id)}
                                                className={`!px-3 !py-1.5 text-sm !rounded-lg ${hasKey
                                                    ? '!bg-white dark:!bg-neutral-800 !text-gray-700 dark:!text-neutral-200 !border !border-gray-200 dark:!border-neutral-700'
                                                    : '!bg-blue-600 hover:!bg-blue-700 !text-white'
                                                    }`}
                                            >
                                                {hasKey ? 'Edit' : 'Add Key'}
                                            </Button>
                                            {hasKey && (
                                                <button
                                                    onClick={() => handleRemove(provider.id)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                >
                                                    <BiTrash />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                onClick={() => handleSave(provider.id)}
                                                className="!px-3 !py-1.5 text-sm !rounded-lg !bg-blue-600 hover:!bg-blue-700 !text-white"
                                            >
                                                Save
                                            </Button>
                                            <button
                                                onClick={() => { setEditingProvider(null); setTempKey(''); }}
                                                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                                            >
                                                <BiX className="text-lg" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {isEditing && (
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-neutral-700">
                                        <TextInputLabelForm
                                            id={`${provider.id}-key`}
                                            label="API Key"
                                            placeholder={provider.placeholder}
                                            value={tempKey}
                                            onChange={setTempKey}
                                            type="password"
                                            size="sm"
                                        />
                                        <p className="text-xs text-gray-500 dark:text-neutral-400 mt-2">
                                            Get your API key at{' '}
                                            <a
                                                href={provider.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                            >
                                                {provider.url}
                                            </a>
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </SectionSettingsWrapper>
    );
};

// =============================================================================
// BUILT-IN TOOLS SECTION
// =============================================================================

const BuiltInToolsSection: React.FC = () => {
    const [toolsConfig, setToolsConfig] = useAtom(globalToolsConfigAtom);
    const [searchQuery, setSearchQuery] = useState('');

    const enabledTools = toolsConfig.enabledTools;

    const toggleTool = useCallback((toolName: string) => {
        setToolsConfig(prev => {
            const isEnabled = prev.enabledTools.includes(toolName);
            return {
                ...prev,
                enabledTools: isEnabled
                    ? prev.enabledTools.filter((t: string) => t !== toolName)
                    : [...prev.enabledTools, toolName],
            };
        });
    }, [setToolsConfig]);

    const enableAllTools = useCallback(() => {
        const allEnabledTools = Object.entries(toolMetadata)
            .filter(([_, meta]) => meta.enabled)
            .map(([name]) => name);
        setToolsConfig(prev => ({ ...prev, enabledTools: allEnabledTools }));
    }, [setToolsConfig]);

    const disableAllTools = useCallback(() => {
        setToolsConfig(prev => ({ ...prev, enabledTools: [] }));
    }, [setToolsConfig]);

    const filteredTools = useMemo(() => {
        return Object.entries(toolMetadata).filter(([name, meta]) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return (
                name.toLowerCase().includes(query) ||
                meta.name.toLowerCase().includes(query) ||
                meta.description.toLowerCase().includes(query)
            );
        });
    }, [searchQuery]);

    return (
        <SectionSettingsWrapper title="Built-in Tools" isShowTitle={true}>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500 dark:text-neutral-400">
                        Enable or disable AI tools that extend your assistant&apos;s capabilities.
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={enableAllTools}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        >
                            Enable all
                        </button>
                        <button
                            onClick={disableAllTools}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                        >
                            Disable all
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search tools..."
                        className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Tools grid */}
                <div className="grid gap-2">
                    {filteredTools.map(([toolKey, tool]) => {
                        const isEnabled = enabledTools.includes(toolKey);
                        const isAvailable = tool.enabled;

                        return (
                            <div
                                key={toolKey}
                                onClick={() => isAvailable && toggleTool(toolKey)}
                                className={`
                                    flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer
                                    ${isEnabled
                                        ? 'bg-blue-50 dark:bg-neutral-900 border-blue-200 dark:border-blue-700'
                                        : 'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-800 hover:border-gray-300 dark:hover:border-neutral-700'
                                    }
                                    ${!isAvailable ? 'opacity-60 cursor-not-allowed' : ''}
                                `}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`
                                        w-9 h-9 rounded-lg flex items-center justify-center text-base
                                        ${isEnabled
                                            ? 'bg-blue-100 dark:bg-blue-800/40'
                                            : 'bg-gray-100 dark:bg-neutral-800'
                                        }
                                    `}>
                                        {tool.icon}
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${isEnabled
                                            ? 'text-blue-900 dark:text-blue-100'
                                            : 'text-gray-900 dark:text-white'
                                            }`}>
                                            {tool.name}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            {tool.description}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!isAvailable && (
                                        <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded-lg">
                                            Coming Soon
                                        </span>
                                    )}
                                    {isAvailable && (
                                        <div className={`
                                            relative w-10 h-5 rounded-full transition-colors
                                            ${isEnabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-neutral-600'}
                                        `}>
                                            <div className={`
                                                absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform
                                                ${isEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                                            `} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="text-xs text-gray-500 dark:text-neutral-400 text-center">
                    {enabledTools.length} of {Object.keys(toolMetadata).length} tools enabled
                </p>
            </div>
        </SectionSettingsWrapper>
    );
};

// =============================================================================
// MCP SERVERS SECTION
// =============================================================================

const MCPServersSection: React.FC = () => {
    const { isAllowMCP } = useUserSubscription();
    const setUpgradeModal = useSetAtom(upgradeModalAtom);
    const [mcpServers, setMcpServers] = useAtom(mcpServersConfigAtom);
    const [isAdding, setIsAdding] = useState(false);
    const [newServer, setNewServer] = useState<Partial<MCPServerUIConfig>>({
        name: '',
        url: '',
        type: 'http',
    });

    const handleAddServer = useCallback(() => {
        if (!isAllowMCP.allow) {
            toast.error(isAllowMCP.message);
            setUpgradeModal({ isOpen: true, lastOpen: new Date() });
            return;
        }
        if (!newServer.name || !newServer.url) return;

        const server: MCPServerUIConfig = {
            id: `mcp-${Date.now()}`,
            name: newServer.name,
            url: newServer.url,
            type: (newServer.type as 'http' | 'sse') || 'http',
            enabled: true,
        };

        setMcpServers([...mcpServers, server]);
        setNewServer({ name: '', url: '', type: 'http' });
        setIsAdding(false);
    }, [isAllowMCP.allow, isAllowMCP.message, newServer, mcpServers, setMcpServers, setUpgradeModal]);

    const toggleServer = useCallback((id: string) => {
        setMcpServers(mcpServers.map(s =>
            s.id === id ? { ...s, enabled: !s.enabled } : s
        ));
    }, [mcpServers, setMcpServers]);

    const removeServer = useCallback((id: string) => {
        setMcpServers(mcpServers.filter(s => s.id !== id));
    }, [mcpServers, setMcpServers]);

    return (
        <SectionSettingsWrapper title="MCP Servers" isShowTitle={true}>
            <div className="space-y-4">
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                            <BsGlobe className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Model Context Protocol (MCP)
                            </p>
                            <p className="text-xs text-gray-600 dark:text-neutral-400 mt-1">
                                Connect to MCP servers to add custom tools, data sources, and capabilities to your AI assistant.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Server list */}
                {mcpServers.length > 0 && (
                    <div className="space-y-2">
                        {mcpServers.map(server => (
                            <div
                                key={server.id}
                                className={`
                                    flex items-center justify-between p-4 rounded-xl border transition-all
                                    ${server.enabled
                                        ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/50'
                                        : 'bg-gray-50 dark:bg-neutral-800/50 border-gray-200 dark:border-neutral-700'
                                    }
                                `}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`
                                        w-8 h-8 rounded-lg flex items-center justify-center
                                        ${server.enabled
                                            ? 'bg-emerald-100 dark:bg-emerald-800/40'
                                            : 'bg-gray-100 dark:bg-neutral-700'
                                        }
                                    `}>
                                        {server.enabled ? (
                                            <BsCheck2Circle className="text-emerald-600 dark:text-emerald-400" />
                                        ) : (
                                            <BsXCircle className="text-gray-400" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {server.name}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-neutral-400 font-mono">
                                            {server.url}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => toggleServer(server.id)}
                                        className={`
                                            px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                                            ${server.enabled
                                                ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                                                : 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
                                            }
                                        `}
                                    >
                                        {server.enabled ? 'Disable' : 'Enable'}
                                    </button>
                                    <button
                                        onClick={() => removeServer(server.id)}
                                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        <BiTrash />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Add new server */}
                {isAdding ? (
                    <div className="p-4 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 space-y-4">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Add New Server</h4>
                        <div className="space-y-3">
                            <TextInputLabelForm
                                id="mcp-name"
                                label="Server Name"
                                placeholder="My MCP Server"
                                value={newServer.name || ''}
                                onChange={(val) => setNewServer(prev => ({ ...prev, name: val }))}
                                size="sm"
                            />
                            <TextInputLabelForm
                                id="mcp-url"
                                label="Server URL"
                                placeholder="https://mcp.example.com/api"
                                value={newServer.url || ''}
                                onChange={(val) => setNewServer(prev => ({ ...prev, url: val }))}
                                size="sm"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleAddServer}
                                disabled={!newServer.name || !newServer.url}
                                className="!bg-blue-600 hover:!bg-blue-700 !text-white flex-1 !rounded-lg disabled:opacity-50"
                            >
                                Add Server
                            </Button>
                            <Button
                                onClick={() => { setIsAdding(false); setNewServer({ name: '', url: '', type: 'http' }); }}
                                className="!bg-gray-200 dark:!bg-neutral-700 !text-gray-700 dark:!text-gray-300 !rounded-lg"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-neutral-600 text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all"
                    >
                        <BsPlus className="text-xl" />
                        <span className="text-sm font-medium">Add MCP Server</span>
                    </button>
                )}
            </div>
        </SectionSettingsWrapper>
    );
};

export default ToolsSettings;
